import {
  IconDavinciResolve,
  IconPremierPro,
  Spacing,
} from '@kezios/design-system';
import { Flex, Stack } from '@mantine/core';
import { ceilPriceToOneDecimal } from '@utils/price.util';
import { PricingSectionLayout } from 'components/website/sections/PricingSection/PricingSection/PricingSectionLayout';
import { planIds, PLANS } from 'consts/planIds.consts';
import { currencyFormatters } from 'lang/priceFormatter';
import { useFormatter, useTranslations } from 'next-intl';

import { CheckItem } from '../../atoms/CheckItem/CheckItem';
import { EnterprisePricingCard } from './EnterprisePricingCard';
import css from './PricingCardsRow.module.css';

const getFeaturesPerPlanAndFormula = (
  t: any,
  plan: string,
  formula: string
) => {
  const features = [
    'silence',
    'j_l_cut',
    'caption',
    'podcast',
    'zoom',
    'autoviral',
    'broll',
    'swear_word',
    'repeat',
    'autochapters',
  ];

  const featuresDisabled = {
    basic: [
      'caption',
      'podcast',
      'zoom',
      'broll',
      'repeat',
      'swear_word',
      'autoviral',
      'autochapters',
    ],
    ai: [],
  };

  const pproCompatibleFeatures = [
    'silence',
    'j_l_cut',
    'caption',
    'podcast',
    'zoom',
    'broll',
    'repeat',
    'swear_word',
    'autoviral',
    'autochapters',
  ];

  const davinciCompatibleFeatures = [
    'silence',
    'j_l_cut',
    'podcast',
    'caption',
    'zoom',
    'broll',
    'repeat',
    'swear_word',
    'autoviral',
    'autochapters',
  ];

  return features.map((feature: string): React.ReactElement => {
    return (
      <CheckItem
        key={`${plan}${formula}${feature}`}
        text={t.rich(
          `cards.${plan}.features.${feature}${
            plan === 'pro' &&
            formula === 'life' &&
            ['caption', 'repeat'].includes(feature)
              ? 'Life'
              : ''
          }`,
          { sm: (chunks: any) => <span className={css.small}>{chunks}</span> }
        )}
        isDisabled={featuresDisabled[plan].includes(feature)}
        isBold={
          !featuresDisabled[plan].includes(feature) &&
          featuresDisabled['basic'].includes(feature)
        }
        rightSection={
          <Flex align="center" gap={Spacing.s1}>
            {pproCompatibleFeatures.includes(feature) ? (
              <IconPremierPro size={22} />
            ) : (
              <div style={{ height: '22px', opacity: '15%' }}>
                <IconPremierPro size={22} />
              </div>
            )}
            {davinciCompatibleFeatures.includes(feature) ? (
              <IconDavinciResolve size={22} />
            ) : (
              <div style={{ height: '22px', opacity: '15%' }}>
                <IconDavinciResolve size={22} />
              </div>
            )}
          </Flex>
        }
      />
    );
  });
};

const getTitle = (
  t: any,
  data: any,
  currency: string,
  format: any,
  currencyFormatterConfig: any,
  formula: string,
  plan: string
) => {
  if (formula === 'life' && plan === 'expert') {
    return `${format.number(
      data[planIds.life.pro[currency]].price / 100,
      currencyFormatterConfig
    )}\n+${format.number(
      data[planIds.life.expert[currency]].price / 100,
      currencyFormatterConfig
    )}${t('per.month')}`;
  }

  const priceId = planIds[formula][plan][currency];
  if (!priceId) return '';
  const priceData = data[priceId];

  if (formula === 'year') {
    return `${
      priceData && priceData.price
        ? format.number(
            ceilPriceToOneDecimal(priceData.price / 12 / 100),
            currencyFormatterConfig
          )
        : ''
    }${t(`per.month`)}`;
  }

  return `${
    priceData && priceData.price
      ? format.number(
          ceilPriceToOneDecimal(priceData.price / 100),
          currencyFormatterConfig
        )
      : ''
  }${t(`per.${formula}`)}`;
};

const getSubtitle = (
  t: any,
  data: any,
  currency: string,
  format: any,
  currencyFormatterConfig: any,
  formula: string,
  plan: string
) => {
  if (formula !== 'year') return undefined;

  const priceId = planIds[formula][plan][currency];
  if (!priceId) return undefined;
  const priceData = data[priceId];

  return t(`billed.${formula}`, {
    price:
      priceData && priceData.price
        ? format.number(
            ceilPriceToOneDecimal(priceData.price / 100),
            currencyFormatterConfig
          )
        : '',
  });
};

const getStrikedTitle = (
  t: any,
  data: any,
  currency: string,
  format: any,
  currencyFormatterConfig: any,
  formula: string,
  plan: string
) => {
  const priceId = planIds[formula][plan][currency];
  if (!priceId) return undefined;
  const priceData = data[priceId];

  if (!data || !priceData || !priceData.basePrice) return undefined;

  if (formula !== 'year')
    return format.number(
      ceilPriceToOneDecimal(priceData.basePrice / 100),
      currencyFormatterConfig
    );

  return format.number(
    ceilPriceToOneDecimal(priceData.basePrice / 12 / 100),
    currencyFormatterConfig
  );
};

export type PricingCardsRowProps = {
  formula: string;
  coupon: string | undefined;
  affiliate: string | undefined;
  currency: string;
  data: {
    [key: string]: {
      price: number;
      basePrice: number;
      recurring: boolean;
      currency: string;
    };
  };
};

export const PricingCardsRow = ({
  formula,
  coupon,
  affiliate,
  currency,
  data,
}: PricingCardsRowProps) => {
  const t = useTranslations('section.pricing');

  const format = useFormatter();

  const currencyFormatterConfig = currencyFormatters[currency] as any;

  return (
    <>
      <Stack gap={Spacing.s8}>
        <Flex gap={Spacing.s8} justify="center" wrap="wrap">
          {PLANS.map(planId => {
            const priceId = planIds[formula][planId][currency];
            if (!priceId) return null;

            return (
              <PricingSectionLayout.Card
                key={`${formula}${planId}${currency}`}
                isLoading={!data}
                title={getTitle(
                  t,
                  data,
                  currency,
                  format,
                  currencyFormatterConfig,
                  formula,
                  planId
                )}
                strikedTitle={getStrikedTitle(
                  t,
                  data,
                  currency,
                  format,
                  currencyFormatterConfig,
                  formula,
                  planId
                )}
                subtitle={getSubtitle(
                  t,
                  data,
                  currency,
                  format,
                  currencyFormatterConfig,
                  formula,
                  planId
                )}
                badgeText={planId === 'ai' ? 'Powered by AI' : undefined}
                planName={t(`cards.${planId}.name`)}
                planSubtitle={
                  <Flex gap={Spacing.s2} align="center" justify="center">
                    {t(`cards.${planId}.subtitle`)}
                    <Flex align="center" gap={Spacing.s1}>
                      <IconPremierPro size={24} />
                      <IconDavinciResolve size={24} />
                    </Flex>
                  </Flex>
                }
                itemType="monthly"
                ctaText={t(
                  `cards.${planId}.${
                    formula === 'life' ? 'lifetimeButton' : 'subscriptionButton'
                  }`
                )}
                ctaHref={`/app/payment/?plan=${planId}&formula=${formula}${
                  affiliate ? `&affiliate=${affiliate}` : ''
                }${coupon ? `&coupon=${coupon}` : ''}`}
                features={getFeaturesPerPlanAndFormula(t, planId, formula)}
              />
            );
          })}
        </Flex>
        {/*<EnterprisePricingCard key="enterprise" />*/}
      </Stack>
    </>
  );
};
